import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { BankAccountDataVariables, CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import { useCurrentStep } from 'hooks/useCurrentStep';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { NotFoundPageContent } from 'pages/CuroPages/NotFoundPage/NotFoundPage';
import { TableValue } from 'product_modules/api/Types';
import { handleDeclineApplication, runSecondChanceCashflowAndAutoOrigination } from 'thunks';

/* eslint-disable no-restricted-imports */
import { NoCreditOfferFlowSteps } from '../constants';
import {
  ConnectBankAccountInformationNeededStep,
  BankInformationPlaidIntegrationWaitingStep,
  BankInformationPlaidStep,
  SelectLoanOfferStep,
  BankAccountSelectStep,
  SetupAutoPayFillingOptionsStep,
  SetupAutoPayStep,
} from '../steps';

interface INoCreditOfferFlowProps {
  startBranchFlow: () => void;
}

const NoCreditOfferFlow = ({ startBranchFlow }: INoCreditOfferFlowProps) => {
  const application = useAppSelector(getApplicationSelector)!;
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const trackEvent = useGoogleAnalytics();

  const isPlaidIntegrationsCompleted =
    application.variables[CuroApplicationVariables.PlaidAuthAndIdentityIntegrationsPassed] &&
    application.variables[CuroApplicationVariables.PlaidCraIntegrationsPassed];
  const aorPass = application.variables[CuroApplicationVariables.AorPass];
  const bankAccountData = application.variables[
    CuroApplicationVariables.BankAccountData
  ] as TableValue;

  const [currentStep, setCurrentStep] = useCurrentStep<NoCreditOfferFlowSteps | null>(
    isPlaidIntegrationsCompleted && aorPass
      ? NoCreditOfferFlowSteps.SelectOnlineLoanOffer
      : NoCreditOfferFlowSteps.ConnectBankAccountInformationNeededStep,
  );

  const getIsSeveralBankAccounts = (bankData: TableValue) => {
    return (
      bankData?.filter(
        (account) => account[BankAccountDataVariables.BankAccountType] === 'checking',
      )?.length > 1
    );
  };

  const handlePlaidFailed = async () => {
    if ((application.variables[CuroApplicationVariables.MaxSecured] as number) > 0) {
      startBranchFlow();
    } else {
      await dispatchWithUnwrap(handleDeclineApplication());
    }
  };

  const onCompletePlaid = async () => {
    const updatedApplication = await dispatchWithUnwrap(
      runSecondChanceCashflowAndAutoOrigination({ applicationId: application?.id }),
    );

    if (updatedApplication.variables[CuroApplicationVariables.AorPass] === false) {
      startBranchFlow();
    } else {
      setCurrentStep(NoCreditOfferFlowSteps.SelectOnlineLoanOffer);
    }
    trackEvent(GoogleAnalyticsEventType.OnlineOrigination);
  };

  const handleAcceptOnlineLoanOffer = async () => {
    const nextStep = getIsSeveralBankAccounts(bankAccountData)
      ? NoCreditOfferFlowSteps.BankAccountSelectStep
      : NoCreditOfferFlowSteps.SetupAutoPayFillingOptionsStep;

    setCurrentStep(nextStep);
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case NoCreditOfferFlowSteps.ConnectBankAccountInformationNeededStep:
        return (
          <ConnectBankAccountInformationNeededStep
            onStepComplete={startBranchFlow}
            onPlaid={() => setCurrentStep(NoCreditOfferFlowSteps.BankInformationPlaid)}
          />
        );
      case NoCreditOfferFlowSteps.BankInformationPlaid:
        return (
          <BankInformationPlaidStep
            onStepExit={() =>
              setCurrentStep(NoCreditOfferFlowSteps.ConnectBankAccountInformationNeededStep)
            }
            onStepFailed={() =>
              setCurrentStep(NoCreditOfferFlowSteps.BankInformationPLaidIntegrationFailed)
            }
            onStepComplete={() =>
              setCurrentStep(NoCreditOfferFlowSteps.BankInformationPlaidIntegrationWaiting)
            }
          />
        );
      case NoCreditOfferFlowSteps.BankInformationPlaidIntegrationWaiting:
        return (
          <BankInformationPlaidIntegrationWaitingStep
            onStepFail={handlePlaidFailed}
            onStepComplete={onCompletePlaid}
          />
        );
      case NoCreditOfferFlowSteps.BankInformationPLaidIntegrationFailed:
        return <NotFoundPageContent />;
      case NoCreditOfferFlowSteps.SelectOnlineLoanOffer:
        return <SelectLoanOfferStep variant='online' onStepComplete={handleAcceptOnlineLoanOffer} />;
      case NoCreditOfferFlowSteps.BankAccountSelectStep:
        return (
          <BankAccountSelectStep
            onContinueClick={() =>
              setCurrentStep(NoCreditOfferFlowSteps.SetupAutoPayFillingOptionsStep)
            }
          />
        );
      case NoCreditOfferFlowSteps.SetupAutoPayFillingOptionsStep:
        return (
          <SetupAutoPayFillingOptionsStep
            withBackButton={getIsSeveralBankAccounts(bankAccountData)}
            onContinueClick={() => setCurrentStep(NoCreditOfferFlowSteps.SetupAutoPayStep)}
            onBackButtonClick={() => setCurrentStep(NoCreditOfferFlowSteps.BankAccountSelectStep)}
          />
        );
      case NoCreditOfferFlowSteps.SetupAutoPayStep:
        return (
          <SetupAutoPayStep
            onBackButtonClick={() =>
              setCurrentStep(NoCreditOfferFlowSteps.SetupAutoPayFillingOptionsStep)
            }
          />
        );
      default:
        return null as never;
    }
  };
  return renderCurrentStep();
};

export default NoCreditOfferFlow;
